/*
tslint:disable:import-spacing no-inferrable-types
variable-name align no-trailing-whitespace component-class-suffix
no-string-literal component-selector
*/

import {AfterViewInit, Component, EventEmitter, Input, Output} from '@angular/core';

import {Logger} from '@/util/logger';

import 'ag-grid-community';
import 'ag-grid-enterprise';

import {ServiceManager} from "@/services/service.manager";
import {BatchViewerMetadata} from "@/screens/ide/batch.viewer.metadata";
import {BatchView} from "@/screens/ide/batchviewer/batch.view";
import {first} from "rxjs/operators";
import {BatchEditCellRenderer} from "@/helpers/batch.edit.cell.renderer";
import {BatchRowHeaderRenderer} from "@/helpers/batch.row.header.renderer";
import {BatchCell} from "@/models/batch/batch.cell";
import {basename, empty, errorAsText, fromMySQLDate} from "@/util/util";

import {JSONBrowserMetadata} from "@/screens/ide/json.browser.metadata";
import {ResultCell} from "@/models/batch/result.cell";
import {BatchMonitorCellRenderer} from "@/helpers/batch.monitor.cell.renderer";
import {artifactString, ArtifactType} from "@/models/batch/artifact.type";
import {TextEditorMetadata} from "@/screens/ide/text.editor.metadata";
import * as $ from 'jquery';
import {SimulationJob, SimulationStatusType} from "@/models/sim/simulation.job";
import {ResultPhaseItem} from "@/screens/ide/batchviewer/result.phase.item";
import {GetRowIdParams, PostSortRowsParams} from "ag-grid-enterprise";
import {
  AgGridEvent,
  CellPosition, CellRange, Column,
  GetContextMenuItemsParams,
  GridApi, IRowNode, MenuItemDef, RedrawRowsParams,
  RowNode,
  TabToNextCellParams
} from 'ag-grid-community';

@Component({
  selector: 'batch-viewer',
  template: `
    <div
      id="batch-viewer-wrapper_{{uuid}}"
      class="batch-viewer-wrapper"
      style="display: block; float: left; padding: 8px; width: 100%; height: 100%;">

      <div class="batchMonitorSummaryPanel"  [class.disabled]="editing()">

        <!-- progress bar, overall progress etc. -->

        <div class="batchMonitorProgressNumber" [innerHTML]="progressHTML()">
        </div>

        <div class="batchMonitorIconArea">
          <div class="batchMonitorIcon" [innerHTML]="iconHTML()"></div>
          <div style="clear: both; height: 1px"></div>
          <div class="batchMonitorTotals" [innerHTML]="totalsHTML()"></div>
        </div>

        <div class="batchMonitorDetailArea">
          <div class="batchMonitorSummaryLine">{{summaryHTML()}}</div>
          <div class="batchMonitorProgressBar">
            <div class="batchMonitorProgressBarInner" [style.width]="progressWidth()"></div>
          </div>
          <div class="batchMonitorStateProgress">

            <div class="batchMonitorStateEndtime"><span>{{endTime()}}</span></div>

            <ul class="batchMonitorStateList">
              <li  class="batchMonitorStatePill" [class.completed]="phase.completed" *ngFor="let phase of phases">
                <span class="batchMonitorStateLabel">{{phase.name}}</span>
                <div style="clear: both; height: 4px; width: 100%; display: block;"></div>
                <span  class="batchMonitorStateDuration">{{phase.duration}} s</span>
              </li>
            </ul>

          </div>
        </div>
      </div>

      <span style="height: 32px; width: 100%; display: block;" [innerHTML]="errorText"></span>

      <ag-grid-angular style="width: 100%; height: calc(100% - 96px - 256px); border-top: 1px solid #bbbbbb; border-bottom: 1px solid #bbbbbb;"
        id="ag-grid-bv_{{uuid}}"
        [class.batchViewEdit]="editing()"
        [class.batchViewMonitor]="!editing()"
        class="ag-theme-balham-dark ag-grid-bv"
        [rowData]="rowData"
        [columnDefs]="columnDefs"
        [defaultColDef]="defaultColDef"
        [context]="context"
        [animateRows]="true"
        [enableRangeSelection]="true"

        [allowContextMenuWithControlKey]="true"
        [getContextMenuItems]="getContextMenuItems"

        [tabToNextCell]="tabToNextCell"

        [components]="components"

        [postSortRows]="postSort"
        [getRowId]="getRowNodeId"

        (gridReady)="onGridReady($event)"
        (firstDataRendered)="onDataRendered($event)"

      >
      </ag-grid-angular>

      <div class="batchMonitorHistoryPanel"  [class.disabled]="editing()">

        <!-- progress history -->

        <ul class="batchProgressHistory"></ul>

      </div>
    </div>

   `
})

/**
 *
 * @class BatchViewer
 *
 * editor/viewer for scalable work engine batches
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      February 2022
 *
 */
export class BatchViewer implements AfterViewInit {

  /** common access to the various services */
  public services: ServiceManager       = null;

  public          errorText: string     = '<i class="wbedit-info-line"></i>';

  @Input()             uuid: number     = 0;

  /** bubble up a ready event when AGGrid is ready for use */
  @Output() ready                       = new EventEmitter<{status: boolean}>();

  /** the Scalable Work Engine (SWE) batch ID/OID */
  public batchId: number                = 0;

  /* the Scalable Work Engine (SWE) result ID/OID */
  public resultId: number               = 0;

  /** the name of the batch */
  public name: string                   = '';

  /** the root folder for the batch */
  public root: string                   = ';'

  /** are editing or monitoring? */
  public mode: BatchView                = BatchView.EDIT;

  /** the API for the widget */
  private               api: GridApi    = null;

  private      isFullScreen: boolean    = false;

  private lastResultDef: any            = null;
  private lastSummary: string           = '';
  private resolved: boolean             = false;
  private crash: string[]               = [];
  private baseTime: number              = 0;
  private elapsed: number               = 0;
  private monitorStart: Date            = null;
  private tickBase: number              = 0;
  public phases: ResultPhaseItem[]      = [];

  private cellLUT: Map<string, ResultCell> = new Map<string, ResultCell>();

  private totals: {
    total:     number,
    running:   number,
    failed:    number,
    completed: number,
    remaining: number
  }                            = {
    total:     0,
    running:   0,
    failed:    0,
    completed: 0,
    remaining: 0
  };

  public context  = {

    batchviewer: this

  };

  public defaultColDef           = {
    sortable:  false,
    filter:    true,
    resizable: true
  };

  public rowData: any            = [];

  public components = {
  };

  public columnDefs: any[]       = [
  ];

  constructor(
    services: ServiceManager
  ) {

    this.services = services;
  }

  /** update the error line in the loader drop down */
  public status(msg: string =  '') {

    this.errorText = `<i class="wbedit-info-line">${msg}</i>`;
    Logger.info(`[bv] ${msg}`);
  }

  /** update the error line in the loader drop down */
  public success(msg: string =  '') {

    this.errorText = `<i class="wbedit-success-line">${msg}</i>`;
    Logger.debug(`[bv] ${msg}`);
  }

  public editing(): boolean {

    return this.mode == BatchView.EDIT;
  }

  /** update the error line in the loader drop down */
  public error(msg: string =  '') {

    this.errorText = `<i class="wbedit-error-line">${msg}</i>`;
    Logger.error(`[bv] ${msg}`);
  }

  postSort(params: PostSortRowsParams): void {

    params.nodes.sort((a: RowNode, b: RowNode) => {

      if (a.data.ordering > b.data.ordering) return 1;

      if (a.data.ordering < b.data.ordering) return -1;

      return 0;
    });
  }

  /** use our own (predictable) row IDs */
  getRowNodeId(params: GetRowIdParams) {

    return params.data.rowNum;
  }

  tabToNextCell(params: TabToNextCellParams): CellPosition  {

    /* if we're going outside the table...stay on target, stay on target! */

    if ( (typeof params.nextCellPosition === 'undefined') || (params.nextCellPosition === null)) {

      return params.previousCellPosition;
    }

    return params.nextCellPosition;
  }

  /** when AGGrid is ready, tell everyone this window can be used! */
  onGridReady(params: AgGridEvent) {

    const bv: BatchViewer = this;

    bv.api       = params.api;

    Logger.debug('[bv] grid is ready...');

    bv.ready.emit({status: true});
  }

  /** do column sizing after we actually have column data */
  onDataRendered(params: AgGridEvent) {

    this.api.autoSizeAllColumns();
  }

  public toggleFullScreen(): void {

    if (this.isFullScreen) {

      Logger.debug('[bv]', 'exiting full screen...');

      document.exitFullscreen();

    } else {

      Logger.debug('[bv]', 'going full screen...');

      const elem = document.getElementById(`batch-viewer-wrapper_${this.uuid}`);

      if (elem.requestFullscreen) {

        elem.requestFullscreen();
      }
    }

  }

  progressWidth(): string {

    if (!this.lastResultDef) {

      return `0%`;
    }

    return `${this.lastResultDef.progress}%`;
  }

  /** fetch the overall progress number html */
  public progressHTML(): string {

    if (!this.lastResultDef) {

      return ``;
    }

    return `<span>${Math.floor(parseFloat(this.lastResultDef.progress))}%</span>`
  }

  isFailStatus(): boolean {

    let status: SimulationStatusType = SimulationJob.statusType(this.lastResultDef.status);

    switch(status) {

      case SimulationStatusType.FAIL:
      case SimulationStatusType.FAILED:
      case SimulationStatusType.ABORTED:
      case SimulationStatusType.CANCELED:
      case SimulationStatusType.ERROR:
      case SimulationStatusType.DEATH:
        return true;
    }

    return false;
  }

  isCompleteStatus(): boolean {

    let status: SimulationStatusType = SimulationJob.statusType(this.lastResultDef.status);

    switch(status) {

      case SimulationStatusType.SUCCESS:
      case SimulationStatusType.COMPLETED:
        return true;
    }

    return false;
  }

  /** quick check to see if the job crashed */
  crashed(): boolean {

    return this.crash.length > 0;
  }

  /* fetch the current status */
  state(): SimulationStatusType {

    let status: SimulationStatusType = SimulationJob.statusType(this.lastResultDef.status);

    if ((status != SimulationStatusType.CANCELED) &&
        (status != SimulationStatusType.QUEUED) &&
        (status != SimulationStatusType.READY)) {

      if (!this.resolved) {

        /*
         * if in the middle of processing, and we don't have the remote downloadables yet,
         * then we have to return WORKING, to keep this bastard alive until the downloadable
         * artifacts are ready at the cloud sim service.
         *
         */

        return SimulationStatusType.WORKING;
      }
    }

    return status;
  }

  /** return an image or span that provides the icon for this job, given its current status */
  icon(): string {

    if (this.isFailStatus()||this.isCompleteStatus()) {

      /*
       * as soon as a step in simulation fails, we know the whole thing has failed, but we may still
       * be waiting for other steps to fail out as well, or we may be waiting for the error logs etc.
       * to download.  So don't actually show fail/complete final status, until we are marked as
       * resolved.
       *
       */

      if (!this.resolved) {

        return 'sim-result-working-32.png';
      }

      /* if it finished and resolved, but we detected a crash...than show the job as bombed */

      if (this.crashed()) {

        return 'sim-result-crashed-32.png';
      }

    }

    let status: SimulationStatusType = SimulationJob.statusType(this.lastResultDef.status);

    switch (status) {

      case SimulationStatusType.FAIL:      return 'sim-result-fail-32.png';
      case SimulationStatusType.FAILED:    return 'sim-result-fail-32.png';
      case SimulationStatusType.ABORTED:   return 'sim-result-fail-32.png';
      case SimulationStatusType.DEATH:     return 'sim-result-fail-32.png';
      case SimulationStatusType.ERROR:     return 'sim-result-fail-32.png';
      case SimulationStatusType.CANCELED:  return 'sim-result-fail-32.png';
      case SimulationStatusType.QUEUED:    return 'sim-result-queued-32.png';
      case SimulationStatusType.SUCCESS:   return 'sim-result-success-32.png';
      case SimulationStatusType.COMPLETED: return 'sim-result-success-32.png';
      case SimulationStatusType.WORKING:   return 'sim-result-working-32.png';
      case SimulationStatusType.RUNNING:   return 'sim-result-working-32.png';
      case SimulationStatusType.READY:     return 'sim-result-none-32.png';
    }

    return 'sim-result-none-32.png';
  }

  /** fetch the icon HTML */
  public iconHTML(): string {

    if (!this.lastResultDef) {

      return ``;
    }

    /*
     * icon is based on the status, unless we're failed but have "some" success.
     * if its partially complete, then we show the "some success" icon.
     *
     */

    let icon: string = this.icon();

    if (this.isFailStatus() && this.resolved) {

      /* resolved as failed, but if some completed...we have a partial success */

      if (this.totals.completed > 0) {

        icon = 'sim-result-none-32.png';
      }
    }

    return `<img src="assets/images/${icon}"</img>`
  }

  public summaryHTML(): string {

    if (!this.lastResultDef) {

      return ``;
    }

    return this.lastResultDef.summary;
  }

  public startTime(): string {

    return `${this.baseTime} s`;
  }

  public endTime(): string {

    return `${this.elapsed} s`;
  }

  /* fetch totals summary HTML (to show under the icon) */

  public totalsHTML(): string {

    if (!this.lastResultDef) {

      return ``;
    }

    if (this.isFailStatus()) {

      if (this.totals.completed > 0) {

        return `
<span class="th_line1">${this.totals.completed} of ${this.totals.total}</span>
<span class="th_line2 partial">PARTIAL</span>
`;
      }

      return `
<span class="th_line1">${this.totals.failed} of ${this.totals.total}</span>
<span class="th_line2 failed">FAILED</span>
`;
    } else if (this.isCompleteStatus()) {

      return `
<span class="th_line1">${this.totals.completed} of ${this.totals.total}</span>
<span class="th_line2 completed">COMPLETED</span>
`;
    }

    return `
<span class="th_line1">${this.totals.completed + this.totals.failed} of ${this.totals.total}</span>
<span class="th_line2">WORKING</span>
`;

  }

  /** the right click menu in edit mode */
  public buildEditMenu(params: GetContextMenuItemsParams): (string | MenuItemDef)[] {

    let context: (string | MenuItemDef)[] = [];
    const value: BatchCell                = params.value;

    if (!value) {

      /* no value set, can't do anything */

      return context;
    }

    /* if they right-click on the plant column, don't do anything, it's our "row header" column */

    if (params.column.getColId() == 'plant') {

      return context;
    }

    /* if in edit mode, you can enable/disable */

    if (value.enabled) {

      /* give them the disable action */

      context.push({
        name: `Disable`,
        icon: `<i class="fa fa-times" aria-hidden="true"></i>`,
        action: () => {

          params.context.batchviewer.setCellStatus(params.node, params.column, value, false);
        }
      });

    } else {

      /* give them the enable action */

      context.push({
        name: `Enable`,
        icon: `<i class="fa fa-check" aria-hidden="true"></i>`,
        action: () => {

          params.context.batchviewer.setCellStatus(params.node, params.column, value, true);
        }
       });
    }

    context.push('separator');

    /* in either mode you can view the plant */

    context.push({
      name: `Plant View`,
      icon: `<i class="fas fa-keyboard" aria-hidden="true"></i>`,
      action: () => {

        params.context.batchviewer.viewBlob(value.plantDst);
      }
    });

    context.push({
      name: `Plant Download`,
      icon: `<i class="fas fa-file-download" aria-hidden="true"></i>`,
      action: () => {

        params.context.batchviewer.downloadBlob(value.plantDst);
      }
    });

    /* in either mode you can view the traffic */

    context.push({
      name: `Traffic View`,
      icon: `<i class="fas fa-keyboard" aria-hidden="true"></i>`,
      action: () => {

        params.context.batchviewer.viewBlob(value.trafficDst);
      }
    });

    context.push({
      name: `Traffic Download`,
      icon: `<i class="fas fa-file-download" aria-hidden="true"></i>`,
      action: () => {

        params.context.batchviewer.downloadBlob(value.trafficDst);
      }
    });

    /* export / utility actions */

    context.push('separator');

      /* full screen control */

    let label: string = 'Show Full Screen';

    if (params.context.batchviewer.isFullScreen) {

      label = 'Exit Full Screen';
    }

    context.push( {
      name: label,
      action: () => {

        params.context.batchviewer.toggleFullScreen();
      }
    });

    return context;
  }

  /** the right click menu in monitoring mode */
  public buildMonitorMenu(params: GetContextMenuItemsParams): (string | MenuItemDef)[] {

    let context: (string | MenuItemDef)[] = [];
    const value: ResultCell               = params.value;

    if (!value) {

      /* no value set, can't do anything */

      return context;
    }

    /* do we have any major failures? */

    if (!empty(value.sliceFailure)) {

      context.push({
        name: `Slice Failure`,
        icon: `<i class="fas fa-keyboard" aria-hidden="true"></i>`,
        action: () => {
          params.context.batchviewer.viewTextBlob(value.sliceFailure, 'rawtext');
        }
      });
    }

    if (!empty(value.simFailure)) {

      context.push({
        name: `Sim Failure`,
        icon: `<i class="fas fa-keyboard" aria-hidden="true"></i>`,
        action: () => {
          params.context.batchviewer.viewTextBlob(value.simFailure, 'rawtext');
        }
      });
    }

    if (!empty(value.kpiFailure)) {

      context.push({
        name: `KPI Failure`,
        icon: `<i class="fas fa-keyboard" aria-hidden="true"></i>`,
        action: () => {
          params.context.batchviewer.viewTextBlob(value.kpiFailure, 'rawtext');
        }
      });
    }

    if(context.length > 0) {

      context.push('separator');
    }

    /* give sub-menus for access to artifacts, whatever artifacts are present */

    if (value.hasAnyArtifact([ArtifactType.SCENARIO, ArtifactType.SLICE_ERROR, ArtifactType.SLICE_LOG])) {

      /* needs slice sub-menu */

      let claims: (string | MenuItemDef)[] = [];

      if (value.hasArtifact(ArtifactType.SCENARIO)) {

        claims.push({
          name: `Scenario View`,
          icon: `<i class="fas fa-keyboard" aria-hidden="true"></i>`,
          action: () => {
            params.context.batchviewer.viewBlob(value.artifact(ArtifactType.SCENARIO));
          }
        });

        claims.push({
          name: `Scenario Download`,
          icon: `<i class="fas fa-keyboard" aria-hidden="true"></i>`,
          action: () => {

            params.context.batchviewer.downloadBlob(value.artifact(ArtifactType.SCENARIO));
          }
        });
      }

      if (value.hasArtifact(ArtifactType.SLICE_ERROR)) {

        claims.push({
          name: `Error View`,
          icon: `<i class="fas fa-keyboard" aria-hidden="true"></i>`,
          action: () => {
            params.context.batchviewer.viewTextBlob(value.artifact(ArtifactType.SLICE_ERROR));
          }
        });

        claims.push({
          name: `Error Download`,
          icon: `<i class="fas fa-keyboard" aria-hidden="true"></i>`,
          action: () => {

            params.context.batchviewer.downloadBlob(value.artifact(ArtifactType.SLICE_ERROR));
          }
        });
      }

      if (value.hasArtifact(ArtifactType.SLICE_LOG)) {

        claims.push({
          name: `Log View`,
          icon: `<i class="fas fa-keyboard" aria-hidden="true"></i>`,
          action: () => {
            params.context.batchviewer.viewTextBlob(value.artifact(ArtifactType.SLICE_LOG));
          }
        });

        claims.push({
          name: `Log Download`,
          icon: `<i class="fas fa-keyboard" aria-hidden="true"></i>`,
          action: () => {

            params.context.batchviewer.downloadBlob(value.artifact(ArtifactType.SLICE_LOG));
          }
        });
      }

      context.push({
        name: `Slice Claims`,
        subMenu: claims
      });
    }

    if (value.hasAnyArtifact([ArtifactType.MOVEMENT, ArtifactType.SIM_CMD, ArtifactType.SIM_LOG])) {

      /* needs sim sub-menu */

      let claims: (string | MenuItemDef)[] = [];

      if (value.hasArtifact(ArtifactType.MOVEMENT)) {

        claims.push({
          name: `Movement View`,
          icon: `<i class="fas fa-keyboard" aria-hidden="true"></i>`,
          action: () => {
            params.context.batchviewer.viewBlob(value.artifact(ArtifactType.MOVEMENT));
          }
        });

        claims.push({
          name: `Movement Download`,
          icon: `<i class="fas fa-keyboard" aria-hidden="true"></i>`,
          action: () => {

            params.context.batchviewer.downloadBlob(value.artifact(ArtifactType.MOVEMENT));
          }
        });
      }

      if (value.hasArtifact(ArtifactType.SIM_CMD)) {

        claims.push({
          name: `Command View`,
          icon: `<i class="fas fa-keyboard" aria-hidden="true"></i>`,
          action: () => {
            params.context.batchviewer.viewTextBlob(value.artifact(ArtifactType.SIM_CMD));
          }
        });

        claims.push({
          name: `Command Download`,
          icon: `<i class="fas fa-keyboard" aria-hidden="true"></i>`,
          action: () => {

            params.context.batchviewer.downloadBlob(value.artifact(ArtifactType.SIM_CMD));
          }
        });
      }

      if (value.hasArtifact(ArtifactType.SIM_LOG)) {

        claims.push({
          name: `Log View`,
          icon: `<i class="fas fa-keyboard" aria-hidden="true"></i>`,
          action: () => {
            params.context.batchviewer.viewTextBlob(value.artifact(ArtifactType.SIM_LOG));
          }
        });

        claims.push({
          name: `Log Download`,
          icon: `<i class="fas fa-keyboard" aria-hidden="true"></i>`,
          action: () => {

            params.context.batchviewer.downloadBlob(value.artifact(ArtifactType.SIM_LOG));
          }
        });
      }

      context.push({
        name: `Sim Claims`,
        subMenu: claims
      });
    }

    if (value.hasAnyArtifact([ArtifactType.KPI, ArtifactType.KPIX, ArtifactType.KPI_ERROR])) {

      /* needs kpi sub-menu */

      let claims: (string | MenuItemDef)[] = [];

      if (value.hasArtifact(ArtifactType.KPI)) {

        claims.push({
          name: `KPI View`,
          icon: `<i class="fas fa-keyboard" aria-hidden="true"></i>`,
          action: () => {
            params.context.batchviewer.viewBlob(value.artifact(ArtifactType.KPI));
          }
        });

        claims.push({
          name: `KPI Download`,
          icon: `<i class="fas fa-keyboard" aria-hidden="true"></i>`,
          action: () => {

            params.context.batchviewer.downloadBlob(value.artifact(ArtifactType.KPI));
          }
        });
      }

      if (value.hasArtifact(ArtifactType.KPIX)) {

        claims.push({
          name: `KPIX View`,
          icon: `<i class="fas fa-keyboard" aria-hidden="true"></i>`,
          action: () => {
            params.context.batchviewer.viewBlob(value.artifact(ArtifactType.KPIX));
          }
        });

        claims.push({
          name: `KPIX Download`,
          icon: `<i class="fas fa-keyboard" aria-hidden="true"></i>`,
          action: () => {

            params.context.batchviewer.downloadBlob(value.artifact(ArtifactType.KPIX));
          }
        });
      }

      if (value.hasArtifact(ArtifactType.KPI_ERROR)) {

        claims.push({
          name: `Log View`,
          icon: `<i class="fas fa-keyboard" aria-hidden="true"></i>`,
          action: () => {
            params.context.batchviewer.viewTextBlob(value.artifact(ArtifactType.KPI_ERROR));
          }
        });

        claims.push({
          name: `Log Download`,
          icon: `<i class="fas fa-keyboard" aria-hidden="true"></i>`,
          action: () => {

            params.context.batchviewer.downloadBlob(value.artifact(ArtifactType.KPI_ERROR));
          }
        });
      }

      context.push({
        name: `KPI Claims`,
        subMenu: claims
      });
    }

    if (context.length > 0) {

      context.push('separator');
    }

    /* in either mode you can view the plant */

    context.push({
      name: `Plant View`,
      icon: `<i class="fas fa-keyboard" aria-hidden="true"></i>`,
      action: () => {

        params.context.batchviewer.viewBlob(value.plantDst);
      }
    });

    context.push({
      name: `Plant Download`,
      icon: `<i class="fas fa-file-download" aria-hidden="true"></i>`,
      action: () => {

        params.context.batchviewer.downloadBlob(value.plantDst);
      }
    });

    /* in either mode you can view the traffic */

    context.push({
      name: `Traffic View`,
      icon: `<i class="fas fa-keyboard" aria-hidden="true"></i>`,
      action: () => {

        params.context.batchviewer.viewBlob(value.trafficDst);
      }
    });

    context.push({
      name: `Traffic Download`,
      icon: `<i class="fas fa-file-download" aria-hidden="true"></i>`,
      action: () => {

        params.context.batchviewer.downloadBlob(value.trafficDst);
      }
    });

    /* export / utility actions */

    /* export / utility actions */

    context.push('separator');

    /* full screen control */

    let label: string = 'Show Full Screen';

    if (params.context.batchviewer.isFullScreen) {

      label = 'Exit Full Screen';
    }

    context.push( {
      name: label,
      action: () => {

        params.context.batchviewer.toggleFullScreen();
      }
    });

    return context;
  }

  /** build the right click menu for cells */
  public getContextMenuItems(params: GetContextMenuItemsParams): (string | MenuItemDef)[] {

    const bv: BatchViewer = params.context.batchviewer;

    if (bv.mode == BatchView.EDIT) {

      return bv.buildEditMenu(params);
    }

    return bv.buildMonitorMenu(params);
  }

  /** launch a JSON blob viewing panel */
  public viewBlob(key: string) {

    Logger.debug('[bv] viewing: ', key);

    this.services.ide.launchWindow(
      new JSONBrowserMetadata(
        '',
        '',
        key
      )
    );
  }

  /** launch a TEXT blob viewing panel */
  public viewTextBlob(key: string, mode: string='text') {

    Logger.debug('[bv] (text) viewing: ', key);

    this.services.ide.launchWindow(
      new TextEditorMetadata(
        '',
        '',
        key,
        mode
      )
    );
  }

  /** download a blob for the user */
  public downloadBlob(key: string) {

    Logger.debug('[bv] fetching: ', key);

    /* fetch the blob */

    this.services.blobber.contents(key, 'latest', 'blob')
      .pipe(first())
      .subscribe({
        next: (content: Blob) => {

          Logger.debug(`[bv] downloading ${key}...`);

          const url = window.URL.createObjectURL(content);

          /* set up a fake one-time link with the right download name and the blob attached as the download */

          const element: HTMLAnchorElement = document.createElement('a');
          element.href = url;
          element.download = basename(key);

          element.style.display = 'none';

          document.body.appendChild(element);

          /* do it */

          element.click();

          /* clean up */

          document.body.removeChild(element);

        },

        error: (err) => {

          Logger.error(`[bv] can't find content ${key}: `, errorAsText(err));
        }

      });

  }

  /** @brief first time setup of view */
  ngAfterViewInit() {

    Logger.debug('[bv] view init...');

    this.clear();

    const elem = document.getElementById(`batch-viewer-wrapper_${this.uuid}`);

    const bv: BatchViewer= this;

    elem.addEventListener('fullscreenchange', () => {

      bv.isFullScreen = document.fullscreenElement != null;

      Logger.debug('[bv]', '(elem) flipped full screen mode: ', bv.isFullScreen);

    }, true);

  }

  /** reset */
  public clear() {

    Logger.debug('[bv] resetting...');

    if (!this.api) {

      /* api isn't even available yet */

      return ;
    }

  }

  /** wrap it up! */
  public close() {

    Logger.debug('[bv] CLOSING...');



  }

  /** helper to fetch the current selection */
  public getSelection(): {row: IRowNode, column: Column, cell: BatchCell}[] {

    /*
     * walk through the selected cell ranges, to flatten to just a set of
     * cells and the column (the node) the cell is in. We need the pair, so
     * that when we're done updating we do things setRowData().
     *
     */

    let bv: BatchViewer                                 = this;
    let selection:
      {row: IRowNode, column: Column, cell: BatchCell}[] = [];
    const ranges: CellRange[]                           = bv.api.getCellRanges();

    ranges.forEach((range: CellRange) => {

      const startRow = Math.min(range.startRow.rowIndex, range.endRow.rowIndex);
      const endRow   = Math.max(range.startRow.rowIndex, range.endRow.rowIndex);

      for (let i: number = startRow; i <= endRow; i++) {

        range.columns.forEach(function (column: Column) {

          if (column.getColId() == 'plant') {

            /* can't include row header cells in the selection */

            return ;
          }

          let row: IRowNode   = bv.api.getRenderedNodes().find((x: IRowNode): boolean => x.rowIndex == i);
          let cell: BatchCell = bv.api.getValue(column, row);

          selection.push({row, column, cell})

        });

      } /* end of row walk */

    });

    /* pass back the selection */

    return selection;
  }

  /** enable or disable the selected cells, via right-clicking 'node' and 'value' */
  public setCellStatus(row: IRowNode, column: Column, cell: BatchCell, enabled: boolean) {

    /* from the current selection of cells, pair up those cells and their values ... */

    let bv: BatchViewer                                 = this;
    let selection:
      {row: IRowNode, column: Column, cell: BatchCell}[] = bv.getSelection();

    if (!selection || selection.length === 0) {

      /* only the one they right-clicked on */

      selection.push({row, column, cell});

    }

    /* add progress spinner */

    bv.api.showLoadingOverlay();

    /*
     * walk the cells to build a request to send to SWE to do a big batch
     * enable/disable.
     *
     */

    Logger.debug(`[bv] forcing status on ${selection.length} cells...`);

    bv.services.swe.forceCellStatus(selection.map(x => x.cell), enabled).pipe(first())
      .subscribe({
        next: (content: any) => {

          /* cells in the batch have been updated, now update the cell models ... */

          Logger.debug(`[bv] cell status updated!`, content);

          /* ... scan out the latest status of all cells */

          const statusLUT: Map<number, boolean> = new Map<number, boolean>();

          content.cells.forEach((item: any) => {

            const enabled = item.status == 'enabled';

            statusLUT.set(item.id, enabled);

          });

          /* update the cells we twitched... */

          selection.forEach((sel: { row: IRowNode, column: Column, cell: BatchCell }) => {

            let change: BatchCell = new BatchCell(sel.cell);
            change.enabled = statusLUT.get(sel.cell.id);

          sel.row.setDataValue(sel.column, change);

        });

          const todo: RedrawRowsParams = {
            rowNodes: selection.map(x => x.row).filter((v, i, a) => a.indexOf(v) === i)
          };

          bv.api.redrawRows(todo);

          /* all done! */

          /* remove progress spinner */

          this.api.hideOverlay();

        },

        error: (err) => {

          this.api.hideOverlay();

          Logger.error(`[bv] could not update cell status`, errorAsText(err));
        }

      });

  }

  /** update the view to this batch */
  public refocus(meta: BatchViewerMetadata) {

    const bv: BatchViewer = this;

    bv.batchId = parseInt(meta.batchId);
    bv.name    = meta.name;
    bv.root    = meta.root;
    bv.mode    = meta.mode;

    /* fetch the raw batch info ... */

    if (bv.mode == BatchView.EDIT) {

      Logger.debug(`[bv] editing: `, meta);

      bv.services.swe.batch(bv.batchId.toString()).pipe(first())
        .subscribe({
          next: (content: any) => {

            bv.edit(content);

          },

          error: (err) => {

            Logger.error(`[bv] could not fetch batch definition`, errorAsText(err));
          }

        });

    } else if (bv.mode == BatchView.MONITOR) {

      Logger.debug(`[bv] monitoring: `, meta);

      bv.services.swe.result(bv.batchId.toString()).pipe(first())
        .subscribe({
          next: (content: any) => {

            bv.monitor(content);

          },

          error: (err) => {

            Logger.error(`[bv] could not fetch batch result`, errorAsText(err));
          }

        });
    }
  }

  /** reset the AGGrid to a live progress view of the result */
  public monitor(resultDef: any) {

    const bv: BatchViewer = this;

    bv.lastResultDef = resultDef;

    Logger.debug(`[bv] monitor - starts: `, resultDef);

    bv.phases.push(
      new ResultPhaseItem({name: 'created',    completed: false}),
      new ResultPhaseItem({name: 'uploaded',   completed: false}),
      new ResultPhaseItem({name: 'launched',   completed: false}),
      new ResultPhaseItem({name: 'data_ready', completed: false}),
      new ResultPhaseItem({name: 'downloaded', completed: false}),
      new ResultPhaseItem({name: 'cleaned',    completed: false}),
    );

    /*
     * similar to Edit, except that the cells render result cells instead of
     * batch cells, and we have access to downloaded artifacts at the end.
     *
     * set up the grid as we do for edit(), but this time, overlay the result;
     * any cell that we simulated, we want to decorate with a result cell
     * model; those cells are then the "enabled" cells...the ones that actually
     * simulated.  The others were disabled by some means and were not even
     * included in the batch at Cloud Run.
     *
     */

    /* yank out the metadata from the batch snapshot, so we can see the original axis definition */

    let batchMeta: any = null;

    try {

      batchMeta = JSON.parse(resultDef.batch_snapshot.meta)

    } catch (error) {

      Logger.error(`[bv] monitor - can not decode batch meta data: `, resultDef.batch_snapshot.meta);

      return ;
    }

    if (!batchMeta) {

      Logger.error(`[bv] monitor - no batch meta data`);

      return ;
    }

    Logger.debug(`[bv] monitor - rebuilding batch axis: `, batchMeta);

    /* we have the axis, the cells in the batch_snapshot are not encoded in any special way, just use 'em */

    bv.columnDefs = [];
    bv.rowData    = [];

    this.api.setGridOption('rowData',    bv.rowData);
    this.api.setGridOption('columnDefs', bv.columnDefs);

    let columnLUT: Map<number, any> = new Map<number, any>();
    let    rowLUT: Map<number, any> = new Map<number, any>();

    const xAxis: any[] = batchMeta.gui_layout.x_axis.sort((a: any, b: any): number => {return a.sort_index - b.sort_index});
    const yAxis: any[] = batchMeta.gui_layout.y_axis.sort((a: any, b: any): number => {return a.sort_index - b.sort_index});

    xAxis.forEach((item: any) => {

      columnLUT.set(item.tag, item);
    });

    yAxis.forEach((item: any) => {

      rowLUT.set(item.tag, item);
    });

    /* -=-=-=: Step 1: build column definitions... */

    /* add the row grouping column */

    bv.columnDefs.push({
      headerName: "Plant / Traffic",
      field: 'plant',
      editable: false,
      cellRenderer: BatchRowHeaderRenderer,
      suppressNavigable: true,
      cellStyle: { border: "none", backgroundColor: '#3C3F41' }

    });

    /* add a column for each item in the x-axis ... */

    xAxis.forEach((item: any) => {

      bv.columnDefs.push({
               colId: `_${item.sort_index}`,
          headerName: item.name,
               field: `_${item.sort_index}`,
            editable: false,
            minWidth: 150,
        cellRenderer: BatchMonitorCellRenderer,
      });

    });

    /* group the cells into their rows, and fetch the sort order, so we know which column to place in */

    const cells: any[]              = resultDef.batch_snapshot.cells;
    let cellMap: Map<number, any[]> = new Map<number, any[]>();

    /* pre-scan the cells to convert the metadata (encoded as a JSON string) to an object */

    cells.forEach( (item: any) => {

      if (typeof item.meta != 'string') {

        return;
      }

      try {

        item.meta = JSON.parse(item.meta);

      } catch (error) {

        Logger.error(`[bv] monitor - can not parse cell meta ${item.name}: `, error);
      }

    });

    /* walk the cells to actually create the model for each one ... */

    cells.forEach( (item: any) => {

      let x = columnLUT.get(item.meta.ct[0]);
      let y = rowLUT.get(item.meta.ct[1]);

      if (!x || !y) {

        /* maybe reversed? */

        x = columnLUT.get(item.meta.ct[1]);
        y = rowLUT.get(item.meta.ct[0]);

        if (!x || !y) {

          /* bad cell co-ords? */

          Logger.warning(`[bv] monitor - can't find row/column for cell (will skip): `, item);

          return;
        }
      }

      /* which "row"? */

      const row = y.sort_index;

      if (!cellMap.has(row)) {

        cellMap.set(row, []);
      }

      /*
       * build a basic definition of the cell, this is the disabled version.  We extend it
       * to the enabled version below, if, there is actually a corresponding result cell.
       *
       */

      let model: ResultCell = new ResultCell(
        {
          id:            parseInt(item.id),
          batchId:       parseInt(item.batch_id),
          resultId:      0,
          name:          item.name,
          column:        x.sort_index,
          row,
          colTag:        x.tag,
          rowTag:        y.tag
        }
      );

      bv.cellLUT.set(model.name, model);

      /* save the model to its row */

      cellMap.get(row).push(model);

      /*
       * Note we don't have to order the cells within a row; the column ordering is
       * already set, and the row data fields map to columns by name.  i.e. _${x.tag}
       *
       */


    });

    bv.baseTime           = 0;
    bv.elapsed            = 0;

    /* now for all the cells we actually (or will) simulated, extend the basic models to full models */

    this.update(resultDef);

    /* -=-=-=: Step 2: create the rows (y-axis) ... */

    cellMap.forEach((cells: ResultCell[]) => {

      let rd: any         = {};
      const y: any        = rowLUT.get(cells[0].rowTag);

      /* set the row grouping column cell */

      rd.ordering         = y.sort_index;
      rd.rowNum           = `_${y.sort_index}`;
      rd['plant']         = y.name;

      /* add all the normal cells (one for each traffic column) ... */

      cells.forEach((item: ResultCell) => {

        /*
         * the value of each cell is BatchCell...i.e. the model.  The renderer
         * will handle showing whatever needs to be shown, color of the cell,
         * label etc.
         *
         */

        rd[`_${item.column}`] = item;

      });

      /* save the row */

      bv.rowData.push(rd);

    });

    bv.rowData.sort((a: any, b: any): number => {

      if (a.ordering > b.ordering) return 1;
      if (a.ordering < b.ordering) return -1;
      return 0;
    });

    /* render! */

    this.api.setGridOption('rowData',    bv.rowData);
    this.api.setGridOption('columnDefs', bv.columnDefs);

    /*
     * for the elapsed seconds, ensure it gets updated once a second so the user
     * can see the panel is live.
     *
     */

    setTimeout(() => {

      bv.updateElapsed();

    }, 1000);
  }

  /* update the header area */
  public updateSummary() {

    Logger.debug(`[bv] updating summary...`);




  }

  public updateHistory() {

    const bv: BatchViewer = this;

    Logger.debug(`[bv] updating history...`);

    /* append the current status to the history panel...as long as It's not already the last item */

    const historyPanel: JQuery = $(`#bv_${bv.uuid} .batchMonitorHistoryPanel`);

    if (!$(historyPanel).is('div')) {

      Logger.debug(`[bv] can not find history panel!`);

      return ;
    }

    const history: JQuery = $(historyPanel).find('.batchProgressHistory');

    if (!$(history).is('ul')) {

      Logger.debug(`[bv] can not find history list!`);

      return ;
    }

    /* what is the new status line? */

    let statusLine = '';

    /* for time stamp, the server side times are UTC ... */

    let stamp: Date = fromMySQLDate(bv.lastResultDef.updated_at);

    /* Date.toString() converts to local time */

    let progress = `${bv.lastResultDef.progress}`.padStart(3);

    statusLine = `<li>[${stamp.toLocaleString().padStart(26)}] [${progress}] ${bv.lastResultDef.summary}</li>`;

    /* get the text of the last child */

    if (bv.lastSummary == this.lastResultDef.summary) {

      /* its already set */

      return ;
    }

    bv.lastSummary = this.lastResultDef.summary;

    /* append! */

    $(history).append(statusLine);

    /* always scroll to the bottom */

    $(history).animate({
      scrollTop: $(history).get(0).scrollHeight - $(history).get(0).clientHeight
    }, 500);

  }

  /* update the entire panel using this result data */
  public update(resultDef: any) {

    const bv: BatchViewer = this;

    bv.lastResultDef = resultDef;

    /* update the totals */

    bv.totals = {
      total:     0,
      running:   0,
      failed:    0,
      completed: 0,
      remaining: 0
    };

    bv.lastResultDef.cells.forEach((item: any) => {

      /* check the totals for cell failed/completed */

      if (item.status == 'failed' || item.status == 'crashed' || item.status == 'aborted' || item.status == 'death' || item.status == 'canceled') {

        bv.totals.failed++;

      } else if (item.status == 'completed') {

        bv.totals.completed++;

      } else {

        bv.totals.running++;
      }

      bv.totals.total++;
    });

    bv.totals.remaining = bv.totals.total - bv.totals.failed - bv.totals.completed;

    /* update the cell models that need updating ... */

    Logger.debug(`[bv] updating cells...`);

    resultDef.cells.forEach((item: any) => {

      /* valid cell? */

      const cell: ResultCell = bv.cellLUT.get(item.name);

      if (!cell) {

        return ;
      }

      /* it will or was simulated, fill it in ... */

      bv.extend(cell, item);

    });

    /*
     * check resolution; the result is completely done and resolved, once we get to the cleaned
     * state, and Google has been completely cleaned.
     *
     * once the result is resolved, we can stop watching it, it's not going to change.
     *
     */

    bv.resolved = false;

    const history: any[] = bv.lastResultDef.meta.state_machine.history;

    history.forEach((item: any) => {

      if (item.to == 'cleaned') {

        bv.resolved = true;

        Logger.debug(`[bv] result ${bv.lastResultDef.id} is RESOLVED.`);
      }

    });

    /* force the grid to redraw */

    bv.api.refreshCells({
              force: true,
      suppressFlash: false,
    });

    /* update the result phase details */

    bv.elapsedFromHistory(false);

    /* update the main progress/status display */

    bv.updateSummary();

    /* update the progress history */

    bv.updateHistory();

    if (bv.resolved) {

      /* no further updates required */

      return ;
    }

    /* scheduled the next display refresh */

    setTimeout(() => {

      bv.services.swe.result(bv.lastResultDef.id.toString()).pipe(first())
        .subscribe({
          next: (content: any) => {

            bv.update(content);

          },
          error: (err) => {

            Logger.error(`[bv] could not refresh result`, errorAsText(err));
          }
        });

    }, 1500);

  }

  public elapsedFromHistory(final: boolean) {

    const bv: BatchViewer = this;
    const history: any[]  = bv.lastResultDef.meta.state_machine.history;
    const baseDate: Date  = fromMySQLDate(bv.lastResultDef.created_at);

    history.forEach((item: any, i: number) => {

      let d1: Date;
      let d2: Date = fromMySQLDate(item.timestamp);

      if (i == 0) {

        /* use creation time of result as the starting point */

        d1 = fromMySQLDate(bv.lastResultDef.created_at);

      } else {

        /* use the ending of the previous phase as our start time */

        d1 = fromMySQLDate(history[i-1].timestamp)
      }

      const delta: number = (d2.getTime() / 1000) - (d1.getTime() / 1000);

      const phase: ResultPhaseItem = bv.phases.find(x => x.name == item.to);

      if (!phase) {

        return ;
      }

      phase.start     = (d1.getTime() / 1000) - (baseDate.getTime() / 1000);
      phase.end       = (d2.getTime() / 1000) - (baseDate.getTime() / 1000);
      phase.duration  = delta;
      phase.completed = true;

      if (final || (bv.elapsed < phase.end)) {

        bv.elapsed = phase.end;

        /* reset the between tick counter */

        bv.monitorStart = new Date();
      }

      bv.tickBase = phase.end;

    })
  }

  public updateElapsed() {

    const bv: BatchViewer = this;

    if (bv.monitorStart) {

      const ticks: number = Math.floor(((new Date()).getTime() / 1000) - (bv.monitorStart.getTime() / 1000));

      bv.elapsed = bv.tickBase + ticks;

      /* we're done! */

      if (bv.resolved) {

        /* no further updates required, final elapsed time if rom the final phase of the result completing. */

        bv.elapsedFromHistory(true);

        return;
      }
    }

    /* keep updating */

    setTimeout(() => {

      bv.updateElapsed();

    }, 1000);
  }

  /** given a cell from an actual result, fill in the fresh data */
  public extend(cell: ResultCell, cellData: any) {

    /* just update the model, don't try to do any redrawing etc.  We'll do that in bulk */

    cell.resultId   = cellData.batch_result_id;
    cell.id         = cellData.id;
    cell.status     = cellData.status;
    cell.steps      = cellData.step_progress;
    cell.progress   = cellData.progress_text;
    cell.meta       = cellData.meta;
    cell.trafficDst = cellData.meta.traffic;
    cell.plantDst   = cellData.meta.plant;
    cell.updated    = fromMySQLDate(cellData.updated_at);
    cell.tags       = cellData.meta.ct;

    /* set the error text (usually exceptions) */

    cellData.errors_text.forEach((item: string|object) => {

      if (typeof item == "string") {

        try {

          /* it might be a JSON encoding... */

          const obj: any = JSON.parse(item);

          cell.errors.push(obj);

        } catch {

          /* if we can't parse it, assume simple error */

          cell.errors.push({summary: item});
        }

      } else {

        /* assume its already a valid error object */

        cell.errors.push(item);
      }

    });

    /* set the artifacts */

    if (typeof cellData.meta.artifacts != 'undefined') {

      Object.entries(cellData.meta.artifacts).forEach(([key, value]) => {

        const at: ArtifactType = artifactString(key);

        if (key == 'no-download') {

          return;
        }

        if (at == ArtifactType.None) {

          /* unrecognized */

          Logger.warning(`[bv] extend - unrecognized artifact type: ${key} in cell ${cellData.id}, skipping.`);

          return;
        }

        /* save the claims of that kind */

        cell.artifacts.set(at, value as string[]);
      });
    }

    /* look out for any worker failures we might be able to capture */

    cell.findFailures();

    /* all done! */

    Logger.debug(`[bv] extend - ${cell.id} updated: `, cell);
  }

  /** set a new value for this cell.  Does not auto-redraw */
  public setModel(x: number, y: number, model: BatchCell|ResultCell): boolean {

    const bv: BatchViewer = this;

    /*
     * to find the RowNode, we can use _$x because we implement getRowNodeId() above.
     * to find the column in the row, we can use _$y as the column id, because we used
     * it as colId in the column definition.
     *
     */

    const row: IRowNode = bv.api.getRowNode(`_${y}`);

    if (!row) {

      Logger.error(`[bv] getModel - can not find row (_${x}, _${y})`);

      return null;
    }

    const col: Column = bv.api.getColumn(`_${x}`);

    if (!col) {

      Logger.error(`[bv] getModel - can not find col (_${x}, _${y})`);
      return null;
    }

    row.setDataValue(col, model);

    /* it's been set */

    return true;
  }

  /** fetch the model for this cell, can be either BatchCell or ResultCell */
  public getModel(x: number, y: number): BatchCell|ResultCell|null {

    const bv: BatchViewer = this;

    /*
     * to find the RowNode, we can use _$x because we implement getRowNodeId() above.
     * to find the column in the row, we can use _$y as the column id, because we used
     * it as colId in the column definition.
     *
     */

    const row: IRowNode = bv.api.getRowNode(`_${y}`);

    if (!row) {

      Logger.error(`[bv] getModel - can not find row (_${x}, _${y})`);

      return null;
    }

    const col: Column = bv.api.getColumn(`_${x}`);

    if (!col) {

      Logger.error(`[bv] getModel - can not find col (_${x}, _${y})`);
      return null;
    }

    /* we found it, return it */

    return row.data[col.getColId()];
  }

  /** reset the AGGrid to an edit style view of the batch */
  public edit(batchDef: any) {

    Logger.debug(`[bv] edit - starts: `, batchDef);

    const bv: BatchViewer = this;

    bv.columnDefs = [];
    bv.rowData    = [];

    this.api.setGridOption('rowData',    bv.rowData);
    this.api.setGridOption('columnDefs', bv.columnDefs);

    /*
     * build out an AGGrid with structure:
     *
     *         | traffic 1 | traffic 2 | ...
     * --------|-----------|-----------|---------
     * plant 1 |           |           | ...
     * --------|-----------|-----------|---------
     * plant 2 |           |           | ...
     * --------|-----------|-----------|---------
     *
     * AG Grid does not have such a thing as "Row Header", but we have some options:
     *
     *   1. Dedicate column "0" as the row header column and then use a special cell renderer.
     *   2. Use the row grouping feature...and all groups will have exactly 1 row.
     *
     * The motivation for 2 is that while you don't get special rendering, you get all
     * of the AGGrid built-in features for sorting and filtering etc. that works on
     * groups. There is a downside though; the "group" has to be expanded, even its
     * just one row (as ours always will be)...so...awkward :(
     *
     * We are the beginning of flushing out this whole set of features, so for now...we'll
     * go with row groups, just to keep it as a simple, yet as functional as possible.
     *
     * We'll go with option 1 and define our own "row header", but for now just implement
     * something reasonably simple.
     *
     * In the batch definition, the metadata stores the x-axis and y-axis rendering
     * info we got from the batch control file.  So, we yank that out, and use it to
     * build out our definition of the rows can columns, and similarly we pull the cells
     * from the batch and work backwards (using the tags) to know which cells go where.
     * Once we've built up that structure, we can just format it as row data and insert
     * into AGGrid.
     *
     */

    /* first create some lookup tables ... */

    let columnLUT: Map<number, any> = new Map<number, any>();
    let    rowLUT: Map<number, any> = new Map<number, any>();

    const xAxis: any[] = batchDef.meta.gui_layout.x_axis.sort((a: any, b: any): number => {return a.sort_index - b.sort_index});
    const yAxis: any[] = batchDef.meta.gui_layout.y_axis.sort((a: any, b: any): number => {return a.sort_index - b.sort_index});

    xAxis.forEach((item: any) => {

      columnLUT.set(item.tag, item);
    });

    yAxis.forEach((item: any) => {

      rowLUT.set(item.tag, item);
    });

    /* -=-=-=: Step 1: build column definitions... */

    /* add the row grouping column */

    bv.columnDefs.push({
             headerName: "Plant / Traffic",
                  field: 'plant',
               editable: false,
           cellRenderer: BatchRowHeaderRenderer,
      suppressNavigable: true,
              cellStyle: { border: "none", backgroundColor: '#3C3F41' }

      });

    /* add a column for each item in the x-axis ... */

    xAxis.forEach((item: any) => {

      bv.columnDefs.push({
                  colId: `_${item.sort_index}`,
             headerName: item.name,
                  field: `_${item.sort_index}`,
               editable: false,
           cellRenderer: BatchEditCellRenderer,
      });

    });

    /* group the cells into their rows, and fetch the sort order, so we know which column to place in */

    const cells: any[]              = batchDef.cells;
    let cellMap: Map<number, any[]> = new Map<number, any[]>();

    cells.forEach( (item: any) => {

      let x = columnLUT.get(item.meta.ct[0]);
      let y = rowLUT.get(item.meta.ct[1]);

      if (!x || !y) {

        /* maybe reversed? */

        x = columnLUT.get(item.meta.ct[1]);
        y = rowLUT.get(item.meta.ct[0]);

        if (!x || !y) {

          /* bad cell co-ords? */

          Logger.warning(`[bv] edit - can't find row/column for cell (will skip): `, item);

          return;
        }
      }

      /* which "row"? */

      const row = y.sort_index;

      if (!cellMap.has(row)) {

        cellMap.set(row, []);
      }

      /* build the model for this cell from the raw cell data */

      let model: BatchCell = new BatchCell(
        {
          id:            parseInt(item.id),
          batchId:       parseInt(item.batch_id),
          batchResultId: parseInt(item.batch_result_id),
          errors:        item.errors_text,
          name:          item.name,
          trafficDst:    item.meta.traffic,
          trafficSrc:    '',
          plantDst:      item.meta.plant,
          plantSrc:      '',
          enabled:       item.status.trim().toLowerCase() == 'enabled',
          meta:          item.meta,
          steps:         item.step_progress,
          progress:      item.progress_text,
          column:        x.sort_index,
          row,
          colTag:        x.tag,
          rowTag:        y.tag,
          updated:       fromMySQLDate(item.updated_at)
        }
      );

      /* save the model to its row */

      cellMap.get(row).push(model);

      /*
       * Note we don't have to order the cells with a row; the column ordering is
       * already set, and the row data fields map to columns by name.  i.e. _${x.tag}
       *
       */


    });

    /* -=-=-=: Step 2: create the rows (y-axis) ... */

    cellMap.forEach((cells: BatchCell[]) => {

      let rd: any         = {};
      const y: any        = rowLUT.get(cells[0].rowTag);

      /* set the row grouping column cell */

      rd.ordering         = y.sort_index;
      rd.rowNum           = `_${y.sort_index}`;
      rd['plant']         = y.name;

      /* add all the normal cells (one for each traffic column) ... */

      cells.forEach((item: BatchCell) => {

        /*
         * the value of each cell is BatchCell...i.e. the model.  The renderer
         * will handle showing whatever needs to be shown, color of the cell,
         * label etc.
         *
         */

        rd[`_${item.column}`] = item;

      });

      /* save the row */

      bv.rowData.push(rd);

    });

    bv.rowData.sort((a: any, b: any) => {

      if (a.ordering > b.ordering) return 1;
      if (a.ordering < b.ordering) return -1;
      return 0;
    });

    /* render! */

    this.api.setGridOption('rowData',    bv.rowData);
    this.api.setGridOption('columnDefs', bv.columnDefs);

    /* all done! */

    bv.status(`Editing batch: #${bv.batchId}: ${bv.name}`);
  }
}
